import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const CUSTOMER_SAP_EXPORT_SEARCH_RESOURCE = "customer-sap-export" as const;
export const CUSTOMER_SAP_EXPORT_SEARCH_OPERATION = "search" as const;
export const CUSTOMER_SAP_EXPORT_SEARCH_API_PATH =
  `/v1/${CUSTOMER_SAP_EXPORT_SEARCH_RESOURCE}/${CUSTOMER_SAP_EXPORT_SEARCH_OPERATION}` as const;

export type CustomerSapExportSearchParameters =
  paths[typeof CUSTOMER_SAP_EXPORT_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type CustomerSapExportSearchBody =
  paths[typeof CUSTOMER_SAP_EXPORT_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type CustomerSapExportSearchResponse =
  paths[typeof CUSTOMER_SAP_EXPORT_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

function sanitizeCustomerSapExportSearchBody(
  body: CustomerSapExportSearchBody | undefined,
): CustomerSapExportSearchBody | undefined {
  if (!body) return body;

  const result = { ...body } as CustomerSapExportSearchBody & { exportPeriod?: string[] };
  delete result.exportPeriod;
  return result;
}

export function useCustomerSapExportSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<CustomerSapExportSearchParameters, CustomerSapExportSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    CustomerSapExportSearchResponse,
    Error,
    CustomerSapExportSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<CustomerSapExportSearchParameters, CustomerSapExportSearchBody>>(
      options,
    );

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };
  const body = sanitizeCustomerSapExportSearchBody(optionsWithDefaults.body);

  return useQuery({
    queryKey: [
      CUSTOMER_SAP_EXPORT_SEARCH_RESOURCE,
      CUSTOMER_SAP_EXPORT_SEARCH_OPERATION,
      { parameters: requestParameters, body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<CustomerSapExportSearchResponse>(
        CUSTOMER_SAP_EXPORT_SEARCH_API_PATH,
        body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
